import React, { useState, useEffect, useRef, useContext } from 'react';
import { useQuery } from '@apollo/client';
import styled from 'styled-components';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Button, Popover, Modal } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';

import { EventEmitter, getTranslatedText, AppContext } from '@nile/components';
import { AccountClient } from '@nile/graphql';
import {
  getClientImportFields,
  getQuery as getClientQuery,
  callQuery as callClientQuery,
  shapeOptions as shapeClientOptions,
  getClientValidationFields,
  modelClientValidationFields
} from '@nile/components/src/DromoUploader/Fields/clientFields';
import {
  getRequesterImportFields,
  getQuery as getRequesterQuery,
  callQuery as callRequesterQuery,
  shapeOptions as shapeRequesterOptions,
  getRequesterValidationFields,
  modelRequesterValidationFields
} from '@nile/components/src/DromoUploader/Fields/requesterFields';
import {
  getSiteImportFields,
  getQuery as getSiteQuery,
  callQuery as callSiteQuery,
  shapeOptions as shapeSiteOptions,
  getSiteValidationFields,
  modelSiteValidationFields
} from '@nile/components/src/DromoUploader/Fields/siteFields';
import {
  getFieldType,
  getSelectOptions,
  getValidators,
  intlConverter,
  getFieldDescription
} from '@nile/components/src/DromoUploader/utils/helpers';
import { GET_CUSTOMER_IDENTITY_TOKEN } from '@nile/components/src/DromoUploader/dromo';

const SheetWrapper = styled.div`
  button {
    background: transparent;
    border: none;
    border-radius: 2px;
    outline: none;
  }
`;

const fieldsMap = {
  CLIENT: {
    fields: getClientImportFields,
    getQuery: getClientQuery,
    callQuery: callClientQuery,
    shapeOptions: shapeClientOptions,
    customFields: true,
    getValidationFields: getClientValidationFields,
    modelValidationFields: modelClientValidationFields
  },
  REQUESTER: {
    fields: getRequesterImportFields,
    getQuery: getRequesterQuery,
    callQuery: callRequesterQuery,
    shapeOptions: shapeRequesterOptions,
    customFields: true,
    getValidationFields: getRequesterValidationFields,
    modelValidationFields: modelRequesterValidationFields
  },
  SITE: {
    fields: getSiteImportFields,
    getQuery: getSiteQuery,
    callQuery: callSiteQuery,
    shapeOptions: shapeSiteOptions,
    getValidationFields: getSiteValidationFields,
    modelValidationFields: modelSiteValidationFields
  }
};

const getFields = (module, currentProduct) => {
  const moduleFields = ['CLIENT', 'SITE'].includes(module)
    ? fieldsMap[module]?.fields(currentProduct)
    : fieldsMap[module]?.fields;

  const callQuery = fieldsMap[module]?.callQuery;
  const getQuery = fieldsMap[module]?.getQuery;

  const selectFields = moduleFields.reduce((acc, field, idx) => {
    if (field.type === 'select')
      acc[idx] = getQuery(field.key)
        ? callQuery(field.key)
        : field.selectOptions;
    return acc;
  }, {});

  if (fieldsMap[module]?.customFields) {
    selectFields.customFields = callQuery('customFields');
  }

  return selectFields;
};

const DromoImportSheet = ({ sheetsArray, intl }) => {
  const appContext = useContext(AppContext);
  const { currentProduct } = appContext;
  const [active, setActive] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [loadingStates, setLoadingStates] = useState({});
  const [errorStates, setErrorStates] = useState({});
  const [fields, setFields] = useState({});
  const [validationData, setValidationData] = useState({});
  const [validationDataLoading, setValidationDataLoading] = useState({});
  const [validationDataError, setValidationDataError] = useState({});

  // Refs
  const loaderRef = useRef();
  loaderRef.current = loadingStates;
  const errorRef = useRef();
  errorRef.current = errorStates;
  const fieldsRef = useRef();
  fieldsRef.current = fields;
  const valLoaderRef = useRef();
  valLoaderRef.current = validationDataLoading;
  const valErrorRef = useRef();
  valErrorRef.current = validationDataError;
  const valRef = useRef();
  valRef.current = validationData;

  // Customer identity token
  const { data: token, loading: tokenLoading, error: tokenError } = useQuery(
    GET_CUSTOMER_IDENTITY_TOKEN,
    {
      fetchPolicy: 'network-only',
      client: AccountClient
    }
  );

  // Getting sheets to show
  const sheetsToShow = sheetsArray.filter(({ show }) => show);
  // Sheets with validation
  const sheetsWithValidation = sheetsArray.filter(
    ({ show, customValidation }) => show && customValidation
  );

  const toggleActive = () => setActive(!active);

  const toggleConfirmationActive = () => setConfirmationOpen(!confirmationOpen);

  const closeMenu = () => setActive(false);

  useEffect(() => {
    EventEmitter.subscribe('closeCSV', closeMenu);

    // Setting the loading map
    const loadingMap = {};
    sheetsToShow.forEach((sheet) => {
      loadingMap[sheet.name] = true;
    });
    setLoadingStates(loadingMap);

    // Setting fields for sheets
    sheetsToShow.forEach((sheet) => {
      const promiseMap = getFields(sheet.name, currentProduct);
      Promise.all(Object.values(promiseMap))
        .then((options) => {
          let initFields = ['CLIENT', 'SITE'].includes(sheet.name)
            ? fieldsMap[sheet.name]?.fields()
            : fieldsMap[sheet.name]?.fields;
          options.forEach((set, idx) => {
            const indexToModify = Object.keys(promiseMap)[idx];

            // Adding custom fields
            if (indexToModify === 'customFields') {
              const customFieldsToAdd =
                set.data?.getAllCustomFields?.map((cf) => {
                  const fieldDataObj = {
                    label: cf.label,
                    key: cf.columnName,
                    type: getFieldType(cf),
                    selectOptions: getSelectOptions(cf),
                    validators: getValidators(cf)
                  };
                  const fieldDescription = getFieldDescription(intl, cf);
                  if (fieldDescription) {
                    fieldDataObj.description = fieldDescription;
                  }
                  return fieldDataObj;
                }) || [];
              initFields = [...initFields, ...customFieldsToAdd];
              return;
            }

            // Shaping options for multiselect fields
            const { shapeOptions } = fieldsMap[sheet.name];
            initFields[indexToModify].selectOptions = shapeOptions(
              initFields[indexToModify].key,
              set
            );
          });
          setFields({
            ...fieldsRef.current,
            [sheet.name]: intlConverter(intl, initFields)
          });
          setLoadingStates({ ...loadingStates.current, [sheet.name]: false });
        })
        .catch((e) => {
          setLoadingStates({ ...loadingStates.current, [sheet.name]: false });
          setErrorStates({ ...errorStates.current, [sheet.name]: true });
          console.log(e);
        });
    });

    return () => {
      EventEmitter.unsubscribe('closeCSV');
    };
  }, []);

  useEffect(() => {
    // Setting the loading map
    const loadingMap = {};
    sheetsWithValidation.forEach((sheet) => {
      loadingMap[sheet.name] = true;
    });
    setValidationDataLoading(loadingMap);

    sheetsWithValidation.forEach((sheet) => {
      const { getValidationFields, modelValidationFields } = fieldsMap[
        sheet.name
      ];
      const promiseMap = getValidationFields();
      Promise.all(Object.values(promiseMap))
        .then((valDatas) => {
          const valMap = {};
          valDatas.forEach((set, idx) => {
            valMap[Object.keys(promiseMap)[idx]] = modelValidationFields(
              Object.keys(promiseMap)[idx],
              set
            );
          });
          setValidationData({ ...valRef.current, [sheet.name]: valMap });
          setValidationDataLoading({
            ...valLoaderRef.current,
            [sheet.name]: false
          });
        })
        .catch((e) => {
          setValidationDataLoading({
            ...valLoaderRef.current,
            [sheet.name]: false
          });
          setValidationDataError({
            ...valErrorRef.current,
            [sheet.name]: true
          });
          console.log(e);
        });
    });
  }, []);

  if (!sheetsToShow.length) return null;

  const sheetsLoading = sheetsToShow.reduce(
    (acc, { name }) => acc && loaderRef.current?.[name],
    true
  );
  const sheetsError = sheetsToShow.reduce(
    (acc, { name }) => acc && errorRef.current?.[name],
    true
  );

  const validationLoading = sheetsWithValidation.reduce(
    (acc, { name }) => acc && valLoaderRef.current?.[name],
    true
  );
  const validationError = sheetsWithValidation.reduce(
    (acc, { name }) => acc && valErrorRef.current?.[name],
    true
  );

  if (sheetsError || validationError || tokenError) return null;

  const activator = (
    <Button
      onClick={toggleActive}
      icon={<SVGIcon name="Import" />}
      loading={sheetsLoading || validationLoading || tokenLoading}
      disabled={sheetsLoading || validationLoading || tokenLoading}
    >
      <FormattedMessage id="dromo.csv.import" />
    </Button>
  );

  console.log('validationData => ', validationData);

  return (
    <Popover
      active={active}
      activator={
        <div>
          {activator}
          <Modal
            open={confirmationOpen}
            onClose={toggleConfirmationActive}
            variant="confirmation"
            primaryAction={{
              content: getTranslatedText(intl, 'dromo.confirmation.okay'),
              onAction: toggleConfirmationActive
            }}
            showCloseButton
            small
          >
            <Modal.Section>
              <FormattedMessage id="dromo.import.submit.notification" />
            </Modal.Section>
          </Modal>
        </div>
      }
      preferredAlignment="right"
      onClose={closeMenu}
    >
      <SheetWrapper className="pt-5 pb-5 pr5 pl5 flex fcol">
        {sheetsArray.map((sheet) =>
          sheet.show ? (
            <sheet.component
              onResults={() => {
                toggleActive();
                toggleConfirmationActive();
              }}
              onCancel={toggleActive}
              fields={fieldsRef.current?.[sheet.name]}
              validationProps={validationData[sheet.name]}
              userToken={token?.getCustomerIdentityToken}
            />
          ) : null
        )}
      </SheetWrapper>
    </Popover>
  );
};

export default injectIntl(DromoImportSheet);
